import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import StatusBadge from "../../components/expenses/StatusBadge";
import {
  ArrowLeft,
  Loader2,
  CalendarDays,
  Receipt,
  Stethoscope,
  Trash2,
  Pencil,
  Send,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { formatINR } from "../../lib/expenseOptions";
import { formatISTDate, formatISTDateTime } from "../../lib/datetime";
import { joinParts, NO_VALUE } from "../../lib/vocabulary";
import { toast } from "sonner";

export default function ExpenseDetailPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMode, setActionMode] = useState(null);
  const [remark, setRemark] = useState("");
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/expenses/${id}`);
      setExpense(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function submitDraft() {
    try {
      await api.put(`/expenses/${id}/submit`);
      toast.success("Submitted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function deleteDraft() {
    if (!confirm("Delete this draft?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Deleted");
      navigate("/expenses", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function submitAction() {
    if (!actionMode) return;
    if (actionMode.kind === "reject" && !remark.trim()) {
      toast.error("Please add a reason for rejection.");
      return;
    }
    setActing(true);
    try {
      await api.put(`/expenses/${id}/${actionMode.kind}`, {
        remark: remark.trim() || null,
      });
      toast.success(actionMode.kind === "approve" ? "Expense approved" : "Expense rejected");
      setActionMode(null);
      setRemark("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-callisto-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading expense…
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div className="bg-white border border-callisto rounded-xl p-8 text-center">
        <h2 className="text-lg font-bold text-callisto-navy">Expense unavailable</h2>
        <p className="mt-2 text-sm text-callisto-muted">{error || "Not found"}</p>
        <Link
          to="/expenses"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-callisto-gold hover:underline"
        >
          <ArrowLeft size={14} /> Back to expenses
        </Link>
      </div>
    );
  }

  const isOwner = user?.role === "sales_officer" && expense.sales_officer_id === user.id;
  const isDraft = expense.status === "Draft";
  const isSubmitted = expense.status === "Submitted";
  const isDecided = expense.status === "Approved" || expense.status === "Rejected";
  // Backend-computed: authority minus self-approval, same rule as the queue.
  const canApprove = expense.can_approve === true;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="expense-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/expenses" className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy">
          <ArrowLeft size={14} />
          Back to expenses
        </Link>
        {isOwner && isDraft && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/expenses/${id}/edit`)}
              className="h-10 border-callisto"
              data-testid="expense-edit-button"
            >
              <Pencil className="h-4 w-4 mr-1.5" /> Edit
            </Button>
            <Button
              onClick={submitDraft}
              className="h-10 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="expense-submit-button"
            >
              <Send className="h-4 w-4 mr-1.5" /> Submit
            </Button>
            <Button
              variant="outline"
              onClick={deleteDraft}
              className="h-10 border-red-200 text-red-700 hover:bg-red-50"
              data-testid="expense-delete-button"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          </div>
        )}
        {canApprove && isSubmitted && (
          <div className="flex gap-2">
            <Button
              onClick={() => setActionMode({ kind: "approve" })}
              className="h-10 px-6 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="expense-approve-button"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
            </Button>
            <Button
              onClick={() => setActionMode({ kind: "reject" })}
              variant="outline"
              className="h-10 px-6 border-red-200 text-red-700 hover:bg-red-50"
              data-testid="expense-reject-button"
            >
              <XCircle className="h-4 w-4 mr-1.5" /> Reject
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-4xl font-bold text-callisto-navy">{formatINR(expense.amount)}</p>
            <p className="mt-1 text-sm text-callisto-muted">
              {expense.expense_type} ·{" "}
              <span className="inline-flex items-center gap-1" data-testid="expense-detail-date">
                <CalendarDays size={13} />
                {formatISTDate(expense.expense_date)}
              </span>
            </p>
            <p className="mt-1 text-[11px] uppercase tracking-wider text-callisto-muted">
              Filed by{" "}
              <span className="font-semibold text-callisto-body">{expense.sales_officer_name}</span>
            </p>
          </div>
          <StatusBadge status={expense.status} waitingOn={expense.waiting_on} size="md" />
        </div>
      </div>

      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7 space-y-5">
        <Section title="Details">
          <p className="text-sm text-callisto-body whitespace-pre-wrap">{expense.description}</p>
        </Section>
        {/* THE LINE ITEMS (RC-26). What the claim is actually made of, which is
            what the per-category caps are checked against. Shown whenever the
            row has them; a row the line-item migration has not reached yet
            shows nothing here rather than a fabricated single line — the header
            above already states its category and amount. */}
        {expense.line_items?.length > 0 && (
          <Section title="Line items">
            <ul className="divide-y divide-callisto/60" data-testid="expense-detail-line-items">
              {expense.line_items.map((item, index) => (
                <li key={item.id || index} className="py-2 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-callisto-navy">
                      {item.expense_type}
                    </p>
                    {item.receipt_note && (
                      <p className="text-xs text-callisto-muted">{item.receipt_note}</p>
                    )}
                    {item.receipt_url ? (
                      <a
                        href={fileUrl(item.receipt_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-callisto-gold hover:underline"
                        data-testid={`expense-detail-receipt-${index}`}
                      >
                        <Receipt size={12} /> View receipt
                      </a>
                    ) : null}
                    {/* NO per-line "No receipt uploaded". Receipts attach to
                        the CLAIM now, through one control, and are listed in
                        the Receipts section below — so this line said the
                        opposite of the truth: "No receipt uploaded" directly
                        above two receipts, to the Admin Manager, at the moment
                        they decide whether to verify.

                        The gap is still SHOWN as a gap, once, where the fact
                        actually lives: `expense-detail-no-claim-receipts`.
                        Saying it per line as well would be one fact rendered
                        in two places, disagreeing. */}
                  </div>
                  <p className="text-sm font-bold text-callisto-navy whitespace-nowrap">
                    {formatINR(item.amount)}
                  </p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* THE CLAIM'S RECEIPTS — the bills the rep attached through the ONE
            control on the form. They belong to the CLAIM, not to any single
            line, so this sits OUTSIDE the line-items block above: a row the
            line-item migration has not reached has no line items at all, and
            nesting this inside would hide its bills from the approver
            entirely. Always rendered, so "no receipts" is stated rather than
            left as an absence that reads as a cash claim. */}
        <Section title="Receipts" Icon={Receipt}>
          {expense.receipts?.length ? (
            <ul className="space-y-1" data-testid="expense-detail-claim-receipts">
              {expense.receipts.map((receipt, index) => (
                <li key={receipt.id || index}>
                  <a
                    href={fileUrl(receipt.receipt_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-callisto-gold hover:underline"
                    data-testid={`expense-detail-claim-receipt-${index}`}
                  >
                    <Receipt size={12} /> {receipt.filename || "View receipt"}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            // A GAP IS SHOWN AS A GAP — never a blank that reads as
            // "no bill needed".
            <p
              className="text-xs text-callisto-muted"
              data-testid="expense-detail-no-claim-receipts"
            >
              No receipts attached to this claim
            </p>
          )}
        </Section>

        {expense.receipt_note && (
          <Section title="Receipt note" Icon={Receipt}>
            <p className="text-sm text-callisto-body">{expense.receipt_note}</p>
          </Section>
        )}
        {expense.linked_doctor_name && (
          <Section title="Linked doctor" Icon={Stethoscope}>
            <p className="text-sm text-callisto-body">{expense.linked_doctor_name}</p>
          </Section>
        )}
        {/* The two named signatures (B2). */}
        {expense.verified_by_name && (
          <Section title="Verified by (stage 1)">
            <p
              className="text-sm font-semibold text-callisto-navy"
              data-testid="expense-detail-verified-by"
            >
              {expense.verified_by_name}
            </p>
          </Section>
        )}
        {/* ONE remark treatment, the leave detail page's: the decision names
            its signer, and the remark sits under that name with no heading of
            its own. Two things were wrong with the old block. It was keyed on
            the REMARK, so an approval with no remark showed no approver at all
            — the signature disappeared because the note did. And its title
            ("Rejection remark") restated the status badge at the top of the
            page; the remark's job is to say what the badge does not. */}
        {isDecided && (expense.approver_name || expense.am_remark) && (
          <Section
            title={
              expense.status === "Rejected"
                ? "Rejected by"
                : expense.verified_by_name
                  ? "Approved by (stage 2, final)"
                  : "Approved by (final)"
            }
          >
            {/* A decided row with no name predates approver capture — the
                remark below is still the filer's only "why" and must not go
                down with the missing name. */}
            <p
              className="text-sm font-semibold text-callisto-navy"
              data-testid="expense-detail-approver"
            >
              {expense.approver_name || NO_VALUE}
            </p>
            {expense.am_remark && (
              <p
                className="mt-1 text-sm text-callisto-body whitespace-pre-wrap"
                data-testid="expense-detail-remark"
              >
                {expense.am_remark}
              </p>
            )}
          </Section>
        )}
        {/* THE AUDIT TRAIL — who decided what and when. `toLocaleString()`
            printed the viewer's zone with nothing saying which zone that was,
            and the server stores UTC: a claim approved 00:30 IST read as the
            previous evening on a phone set elsewhere. `formatISTDateTime`
            pins Asia/Kolkata and says so. */}
        <Section title="Timeline">
          <ul className="text-sm text-callisto-body space-y-1" data-testid="expense-detail-timeline">
            <li>
              <span className="text-callisto-muted">Created:</span> {formatISTDateTime(expense.created_at)}
            </li>
            {expense.submitted_at && (
              <li>
                <span className="text-callisto-muted">Submitted:</span> {formatISTDateTime(expense.submitted_at)}
              </li>
            )}
            {expense.verified_at && (
              <li>
                <span className="text-callisto-muted">Verified (stage 1):</span> {formatISTDateTime(expense.verified_at)}
              </li>
            )}
            {expense.approved_at && (
              <li>
                <span className="text-callisto-muted">Approved (final):</span> {formatISTDateTime(expense.approved_at)}
              </li>
            )}
            {expense.payment_release_at && (
              <li>
                <span className="text-callisto-muted">Payment released:</span> {formatISTDateTime(expense.payment_release_at)}
              </li>
            )}
            {expense.rejected_at && (
              <li>
                <span className="text-callisto-muted">Rejected:</span> {formatISTDateTime(expense.rejected_at)}
              </li>
            )}
          </ul>
        </Section>
      </div>

      <Dialog
        open={!!actionMode}
        onOpenChange={(o) => {
          if (!o) {
            setActionMode(null);
            setRemark("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionMode?.kind === "approve" ? "Approve expense" : "Reject expense"}
            </DialogTitle>
            <DialogDescription>
              {joinParts([
                expense.sales_officer_name,
                expense.expense_type,
                formatINR(expense.amount),
              ])}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder={actionMode?.kind === "approve" ? "Optional remark" : "Reason for rejection (required)"}
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="expense-remark"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionMode(null);
                setRemark("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              disabled={acting}
              className={
                actionMode?.kind === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
              }
              data-testid="expense-action-confirm"
            >
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : actionMode?.kind === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, Icon, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-2 flex items-center gap-1.5">
        {Icon && <Icon size={12} />}
        {title}
      </p>
      {children}
    </div>
  );
}
